import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMangoApi } from '~/atoms/MangoApi'
import { AgentCustomStatusResponse } from '~/types/AgentCustomStatus'
import {
  AsyncQueryCancellation,
  UseQueryParameters,
} from '~/types/ApiRequestTypes'
import { UserAgentEventLog } from '~/types/UserAgentEventLog'
import { UserAgentEventLogQueryParams } from '~/utils/mangoApiClient/mangoApiClient'

import { AgentStatus, ApiError, PaginatedApiResult } from '@mangovoice/types'

type UseGetAgentStatusParams = {
  extensionId?: number
} & UseQueryParameters<AgentStatus>
export const useGetAgentStatus = ({
  extensionId,
  onQueryStarted,
  onQuerySuccess,
  onQueryError,
  onQueryCancelled,
  options,
}: UseGetAgentStatusParams) => {
  const api = useMangoApi()
  const queryClient = useQueryClient()

  const getQueryKey = () => {
    const key = ['agentStatus', extensionId]
    return key
  }

  const tryCancelQueries: AsyncQueryCancellation = async () => {
    await queryClient.cancelQueries({
      queryKey: getQueryKey(),
    })
  }

  const { data, refetch: _refetch } = useQuery<AgentStatus, ApiError>({
    queryKey: getQueryKey(),
    queryFn: async ({ signal }) => {
      onQueryStarted?.()
      if (extensionId === undefined || extensionId === 0) {
        return Promise.reject({
          status: 400,
          message: 'Invalid extension ID',
        })
      }
      const { ok, data, status } = await api.getQueueAgentStatus(extensionId)
      if (!ok) {
        const errorData = {
          status,
          ...(data as ApiError),
        }

        if (signal.aborted) {
          onQueryCancelled?.()
          return Promise.reject(errorData)
        }

        onQueryError?.(errorData)
        return Promise.reject(errorData)
      }

      const agentStatus = data as AgentStatus
      onQuerySuccess?.(agentStatus)
      return agentStatus
    },
    ...options,
    enabled: options?.enabled ?? false,
    retry: options?.retry ?? false,
  })

  const refetch = async () => {
    tryCancelQueries()
    return (await _refetch()).data
  }

  return { agentStatus: data?.status, refetch }
}

type UseQueueStatusChangeParams = {
  extensionId?: number
} & UseQueryParameters<undefined>

export const useQueueLogin = ({
  extensionId,
  onQueryStarted,
  onQuerySuccess,
  onQueryError,
  options,
}: UseQueueStatusChangeParams) => {
  const api = useMangoApi()

  const queueLoginMutation = useMutation({
    mutationFn: async () => {
      onQueryStarted?.()
      if (extensionId === undefined || extensionId === 0) {
        return Promise.reject({
          status: 400,
          message: 'Invalid extension ID',
        })
      }

      const { ok, status, data } = await api.putQueueLogin(extensionId)

      if (!ok) {
        return Promise.reject({
          status,
          message: 'Failed to login to queue',
          ...(data as ApiError),
        })
      }

      return
    },
    onSuccess: () => {
      onQuerySuccess?.(undefined)
    },
    onError: (error: ApiError) => {
      onQueryError?.(error)
    },
    retry: options?.retry ?? 3,
  })

  const queueLogin = async (): Promise<any> => {
    await queueLoginMutation.mutateAsync()
  }

  return queueLogin
}

export const useQueueLogout = ({
  extensionId,
  onQueryStarted,
  onQuerySuccess,
  onQueryError,
  options,
}: UseQueueStatusChangeParams) => {
  const api = useMangoApi()

  const queueLogoutMutation = useMutation({
    mutationFn: async () => {
      onQueryStarted?.()
      if (extensionId === undefined || extensionId === 0) {
        return Promise.reject({
          status: 400,
          message: 'Invalid extension ID',
        })
      }

      const { ok, status, data } = await api.putQueueLogout(extensionId)

      if (!ok) {
        return Promise.reject({
          status,
          message: 'Failed to logout from queue',
          ...(data as ApiError),
        })
      }

      return
    },
    onSuccess: () => {
      onQuerySuccess?.(undefined)
    },
    onError: (error: ApiError) => {
      onQueryError?.(error)
    },
    retry: options?.retry ?? 3,
  })

  const queueLogout = async (): Promise<any> => {
    await queueLogoutMutation.mutateAsync()
  }

  return queueLogout
}

type UpdateUserAgentStatusParams = UseQueryParameters<undefined>

export const useUpdateUserAgentStatus = ({
  onQueryStarted,
  onQuerySuccess,
  onQueryError,
}: UpdateUserAgentStatusParams) => {
  const api = useMangoApi()

  const updateUserAgentStatusMutation = useMutation({
    mutationFn: async ({
      customStatusId,
      ext_uuid,
      inQueue,
    }: {
      customStatusId: number | null
      ext_uuid?: string
      inQueue?: boolean
    }) => {
      onQueryStarted?.()
      if (ext_uuid === undefined || ext_uuid === '') {
        return Promise.reject('Invalid extension ID')
      }

      const { ok } = await api.putQueueAgentStatus(
        ext_uuid,
        customStatusId,
        inQueue,
      )

      if (!ok) {
        return Promise.reject('Failed to update user agent status')
      }

      return
    },
    onSuccess: () => {
      onQuerySuccess?.(undefined)
    },
    onError: (error: ApiError) => {
      onQueryError?.(error)
    },
  })

  const updateUserAgentStatus = async (
    customStatusId: number | null,
    ext_uuid?: string,
    inQueue?: boolean,
  ) => {
    await updateUserAgentStatusMutation.mutateAsync({
      customStatusId,
      ext_uuid,
      inQueue,
    })
  }

  return updateUserAgentStatus
}

type UseGetUserAgentStatusParams = {
  ext_uuid?: string
} & UseQueryParameters<AgentCustomStatusResponse>
export const useGetUserAgentStatus = ({
  ext_uuid,
  onQueryStarted,
  onQuerySuccess,
  onQueryError,
  onQueryCancelled,
  options,
}: UseGetUserAgentStatusParams) => {
  const api = useMangoApi()
  const queryClient = useQueryClient()

  const getQueryKey = () => {
    const key = ['userAgentStatus', ext_uuid]
    return key
  }

  const tryCancelQueries: AsyncQueryCancellation = async () => {
    await queryClient.cancelQueries({
      queryKey: getQueryKey(),
    })
  }

  const { data, refetch: _refetch } = useQuery<
    AgentCustomStatusResponse,
    ApiError
  >({
    queryKey: getQueryKey(),
    queryFn: async ({ signal }) => {
      onQueryStarted?.()
      if (ext_uuid === undefined || ext_uuid === '') {
        return Promise.reject({
          status: 400,
          message: 'Invalid extension ID',
        })
      }
      const { ok, data, status } = await api.getUserAgentStatus(ext_uuid)
      if (!ok) {
        const errorData = {
          status,
          ...(data as ApiError),
        }

        if (signal.aborted) {
          onQueryCancelled?.()
          return Promise.reject(errorData)
        }

        onQueryError?.(errorData)
        return Promise.reject(errorData)
      }

      onQuerySuccess?.(data as AgentCustomStatusResponse)
      return data as AgentCustomStatusResponse
    },
    ...options,
    enabled: options?.enabled ?? false,
    retry: options?.retry ?? false,
  })

  const refetch = async () => {
    tryCancelQueries()
    return (await _refetch()).data
  }

  return { status: data, refetch }
}

export const useGetAgentStatuses = (pbxId?: number) => {
  const api = useMangoApi()
  return useQuery<PaginatedApiResult<AgentCustomStatusResponse>, ApiError>({
    queryKey: ['agentStatuses'],
    queryFn: async () => {
      const { ok, data } = await api.getAgentStatuses(pbxId)
      if (!ok) {
        return Promise.reject('Failed to fetch agent statuses')
      }
      const response =
        data as unknown as PaginatedApiResult<AgentCustomStatusResponse>
      return {
        count: response.count,
        next: response.next,
        previous: response.previous,
        results: response.results,
      }
    },
  })
}

type UseGetUserAgentEventLogsParams = {
  params?: UserAgentEventLogQueryParams
} & UseQueryParameters<PaginatedApiResult<UserAgentEventLog>>

export const useGetUserAgentEventLogs = ({
  params,
}: UseGetUserAgentEventLogsParams) => {
  const api = useMangoApi()
  return useQuery<PaginatedApiResult<UserAgentEventLog>, ApiError>({
    queryKey: ['userAgentEventLogs'],
    queryFn: async () => {
      const { ok, data } = await api.getUserAgentEventLogs(params)
      if (!ok) {
        return Promise.reject('Failed to fetch user agent event logs')
      }
      return data as PaginatedApiResult<UserAgentEventLog>
    },
  })
}
